import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { FileText } from "lucide-react";

export default function Terms() {
    return (
        <div className="min-h-screen bg-gradient-subtle flex flex-col">
            <SEO
                title="Terms of Service - TranslateLoo AI"
                description="TranslateLoo AI Terms of Service - Usage terms and conditions"
            />
            <Header />

            <main className="flex-1 max-w-4xl mx-auto px-4 py-12 sm:py-16">
                <article className="space-y-8">
                    {/* Header */}
                    <header className="text-center space-y-4 mb-12">
                        <div className="inline-flex p-3 bg-gradient-primary rounded-2xl mb-4">
                            <FileText className="h-10 w-10 text-white" />
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                            Terms of Service
                        </h1>
                        <p className="text-muted-foreground">
                            Last updated: February 2026
                        </p>
                    </header>

                    {/* Content */}
                    <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
                        <Section title="1. Acceptance of Terms">
                            <p>
                                By accessing and using TranslateLoo AI ("the Service"), you agree to be bound by
                                these Terms of Service. If you do not agree to these terms, please do not use
                                the Service.
                            </p>
                        </Section>

                        <Section title="2. Description of Service">
                            <p>
                                TranslateLoo AI provides an AI-powered translation service that allows users to
                                translate text between multiple languages. The Service is provided "as is" and
                                is intended for informational and personal use.
                            </p>
                        </Section>

                        <Section title="3. User Responsibilities">
                            <p>When using the Service, you agree to:</p>
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                                <li>Use the Service only for lawful purposes</li>
                                <li>Not submit content that is illegal, harmful, or violates third-party rights</li>
                                <li>Not attempt to circumvent, disable, or interfere with the Service</li>
                                <li>Not use the Service for automated bulk translations beyond normal use</li>
                                <li>Respect intellectual property rights in all content you submit</li>
                            </ul>
                        </Section>

                        <Section title="4. Translation Accuracy Disclaimer">
                            <p>
                                <strong>Important:</strong> While we strive to provide accurate translations,
                                AI-powered translation has limitations. Translations may contain errors,
                                especially for:
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                                <li>Technical, legal, or medical terminology</li>
                                <li>Idiomatic expressions and cultural nuances</li>
                                <li>Context-dependent meanings</li>
                                <li>Rare or specialized vocabulary</li>
                            </ul>
                            <p className="mt-4">
                                <strong>We strongly recommend professional human translation for critical
                                    documents including legal contracts, medical documents, official certifications,
                                    and safety-critical content.</strong>
                            </p>
                        </Section>

                        <Section title="5. Intellectual Property">
                            <p>
                                The Service, including its design, features, and technology, is owned by
                                TranslateLoo and is protected by intellectual property laws. You retain ownership
                                of the content you submit for translation. By using the Service, you grant us a
                                temporary license to process your content solely for providing the translation.
                            </p>
                        </Section>

                        <Section title="6. Limitation of Liability">
                            <p>
                                To the maximum extent permitted by law, TranslateLoo AI shall not be liable for:
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                                <li>Translation errors or inaccuracies</li>
                                <li>Decisions made based on translations provided by the Service</li>
                                <li>Loss of data or business interruption</li>
                                <li>Any indirect, incidental, or consequential damages</li>
                            </ul>
                        </Section>

                        <Section title="7. Service Availability">
                            <p>
                                We strive to maintain high availability but do not guarantee uninterrupted
                                access to the Service. We may modify, suspend, or discontinue any part of
                                the Service at any time without notice.
                            </p>
                        </Section>

                        <Section title="8. Privacy">
                            <p>
                                Your use of the Service is also governed by our{" "}
                                <a href="/privacy" className="text-primary hover:underline">
                                    Privacy Policy
                                </a>
                                , which describes how we collect, use, and protect your information.
                            </p>
                        </Section>

                        <Section title="9. Modifications to Terms">
                            <p>
                                We reserve the right to modify these Terms at any time. Continued use of the
                                Service after changes constitutes acceptance of the modified Terms. We encourage
                                you to review these Terms periodically.
                            </p>
                        </Section>

                        <Section title="10. Termination">
                            <p>
                                We may terminate or suspend your access to the Service immediately, without
                                prior notice, for conduct that we believe violates these Terms or is harmful
                                to other users, us, or third parties.
                            </p>
                        </Section>

                        <Section title="11. Governing Law">
                            <p>
                                These Terms shall be governed by and construed in accordance with applicable
                                laws. Any disputes arising from these Terms or the Service shall be resolved
                                through appropriate legal channels.
                            </p>
                        </Section>

                        <Section title="12. Contact">
                            <p>
                                For questions about these Terms, please contact us at:{" "}
                                <a
                                    href="mailto:legal@translateloo.com"
                                    className="text-primary hover:underline"
                                >
                                    legal@translateloo.com
                                </a>
                            </p>
                        </Section>
                    </div>
                </article>
            </main>

            <Footer />
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="bg-translator-panel/30 rounded-xl p-6 border border-translator-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{title}</h2>
            <div className="text-muted-foreground space-y-3">{children}</div>
        </section>
    );
}
